import React, { useRef, useEffect, useState, useCallback } from "react";
import {
  type ColumnDef,
  type Row,
  type VisibilityState,
} from "@tanstack/react-table";
import { DataTable } from "@/components/data-table";

interface R1DataTableProps<TData, TValue> {
  table_name: string;
  label?: string;
  columns: ColumnDef<TData, TValue>[];
  data: TData[];
  stats?: React.ReactNode;
  pinnedLeft?: string[];
  columnVisibilityInit?: VisibilityState;
  getRowClassName?: (row: Row<TData>) => string | undefined;
  tableContainerClassName?: string;
  noCard?: boolean;
  fillHeight?: boolean;
  new_item?: React.ReactNode;
  groupHeaderClassName?: string;
  pageSize?: number;
}

/** Marker class added to DataTable's scroll container so we can find and sync it. */
const SCROLL_TARGET = "r1-scroll-target";

/**
 * R1-specific DataTable wrapper with a custom always-visible horizontal scrollbar.
 * The native scrollbar on the DataTable inner container is hidden via .r1-scroll-target CSS;
 * a custom bar is rendered at the bottom of the card and kept in sync via ResizeObserver + scroll events.
 */
export function R1DataTable<TData, TValue>({
  table_name,
  label,
  columns,
  data,
  stats,
  pinnedLeft,
  columnVisibilityInit,
  getRowClassName,
  tableContainerClassName,
  noCard = false,
  fillHeight = false,
  new_item,
  groupHeaderClassName,
  pageSize: pageSizeProp,
}: R1DataTableProps<TData, TValue>) {
  const wrapperRef = useRef<HTMLDivElement>(null);
  const scrollElRef = useRef<HTMLElement | null>(null);
  const dragRef = useRef<{ startX: number; startScroll: number } | null>(null);
  const [thumb, setThumb] = useState({ w: 0, x: 0, show: false });

  const recalc = useCallback(() => {
    const el = scrollElRef.current;
    if (!el) return;
    const { scrollWidth, clientWidth, scrollLeft } = el;
    if (scrollWidth <= clientWidth + 1) {
      setThumb((t) => (t.show ? { w: 0, x: 0, show: false } : t));
      return;
    }
    const w = Math.max((clientWidth / scrollWidth) * clientWidth, 40);
    const maxX = clientWidth - w;
    const maxScroll = scrollWidth - clientWidth;
    const x = maxScroll > 0 ? (scrollLeft / maxScroll) * maxX : 0;
    setThumb((t) =>
      t.show && Math.abs(t.w - w) < 1 && Math.abs(t.x - x) < 0.5
        ? t
        : { w, x, show: true },
    );
  }, []);

  useEffect(() => {
    const wrapper = wrapperRef.current;
    if (!wrapper) return;
    const el = wrapper.querySelector<HTMLElement>(`.${SCROLL_TARGET}`);
    if (!el) return;
    scrollElRef.current = el;
    recalc();
    el.addEventListener("scroll", recalc, { passive: true });
    const ro = new ResizeObserver(recalc);
    ro.observe(el);
    return () => {
      el.removeEventListener("scroll", recalc);
      ro.disconnect();
    };
  }, [recalc]);

  const onThumbDown = useCallback((e: React.MouseEvent) => {
    e.preventDefault();
    const el = scrollElRef.current;
    if (!el) return;
    dragRef.current = { startX: e.clientX, startScroll: el.scrollLeft };

    const onMove = (ev: MouseEvent) => {
      if (!dragRef.current || !scrollElRef.current) return;
      const el = scrollElRef.current;
      const maxScroll = el.scrollWidth - el.clientWidth;
      const w = Math.max(
        (el.clientWidth / el.scrollWidth) * el.clientWidth,
        40,
      );
      const maxX = el.clientWidth - w;
      if (maxX <= 0) return;
      const dx = ev.clientX - dragRef.current.startX;
      el.scrollLeft = Math.max(
        0,
        Math.min(
          maxScroll,
          dragRef.current.startScroll + (dx / maxX) * maxScroll,
        ),
      );
    };
    const onUp = () => {
      dragRef.current = null;
      window.removeEventListener("mousemove", onMove);
      window.removeEventListener("mouseup", onUp);
    };
    window.addEventListener("mousemove", onMove);
    window.addEventListener("mouseup", onUp);
  }, []);

  const onTrackClick = useCallback((e: React.MouseEvent<HTMLDivElement>) => {
    if ((e.target as HTMLElement).dataset.thumb) return;
    const el = scrollElRef.current;
    if (!el) return;
    const rect = e.currentTarget.getBoundingClientRect();
    const clickX = e.clientX - rect.left;
    const maxScroll = el.scrollWidth - el.clientWidth;
    const w = Math.max((el.clientWidth / el.scrollWidth) * el.clientWidth, 40);
    const maxX = el.clientWidth - w;
    if (maxX <= 0) return;
    el.scrollLeft =
      Math.max(0, Math.min(1, (clickX - w / 2) / maxX)) * maxScroll;
  }, []);

  const scrollbar = thumb.show ? (
    <div
      style={{
        height: 6,
        margin: "3px 4px",
        position: "relative",
        borderRadius: 3,
        background: "rgb(241 245 249)",
        flexShrink: 0,
        cursor: "default",
      }}
      onClick={onTrackClick}
    >
      <div
        data-thumb="1"
        onMouseDown={onThumbDown}
        style={{
          position: "absolute",
          top: 0,
          height: "100%",
          width: thumb.w,
          transform: `translateX(${thumb.x}px)`,
          background: "rgba(100, 116, 139, 0.4)",
          borderRadius: 3,
          cursor: "grab",
          userSelect: "none",
        }}
        onMouseEnter={(e) => {
          (e.currentTarget as HTMLDivElement).style.background =
            "rgba(100, 116, 139, 0.65)";
        }}
        onMouseLeave={(e) => {
          (e.currentTarget as HTMLDivElement).style.background =
            "rgba(100, 116, 139, 0.4)";
        }}
      />
    </div>
  ) : null;

  const mergedTableContainerClassName = [
    noCard ? "" : "rounded-none border-t border-slate-200",
    SCROLL_TARGET,
    tableContainerClassName,
  ]
    .filter(Boolean)
    .join(" ");

  const table = (
    <DataTable<TData, TValue>
      table_name={table_name}
      columns={columns}
      data={data}
      label={label}
      stats={stats}
      new_item={new_item}
      pinnedLeft={pinnedLeft}
      enableHorizontalScroll
      fillHeight={fillHeight}
      pageSize={pageSizeProp ?? data.length ?? 10000}
      columnVisibilityInit={columnVisibilityInit}
      getRowClassName={getRowClassName}
      tableClassName="r1-table"
      tableContainerClassName={mergedTableContainerClassName}
      toolbarClassName="px-4"
      groupHeaderClassName={groupHeaderClassName}
    />
  );

  if (noCard)
    return (
      <div
        ref={wrapperRef}
        className={fillHeight ? "flex flex-col flex-1 min-h-0" : "w-full"}
      >
        {table}
        {scrollbar}
      </div>
    );

  return (
    <div
      ref={wrapperRef}
      className="rounded-xl border border-slate-200 bg-white shadow-[0_1px_2px_rgba(15,23,42,0.04)]"
    >
      {table}
      {scrollbar}
    </div>
  );
}
